import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { InformationIcon, PdfIcon, SendIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useMemo } from 'react'
import { toast } from 'sonner'

import { sendContract } from '../../../api/affected-staffs/send-contract'
import { sendContractReminder } from '../../../api/affected-staffs/send-contract-reminder'
import { useObserver } from '../../../lib/contexts/observer'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { AffectedStaff } from '../../../lib/types/models/affectedStaff/affected-staff'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import LabelWithText from '../ols/label-with-text'

type Props = {
  affectedStaff: AffectedStaff
}

const AffectedStaffContractSummarySection = ({ affectedStaff }: Props) => {
  const { modalState, toggleModal, closeModel } = useModalManager<'contract-change-confirmation'>([
    'contract-change-confirmation'
  ])

  const sendContractObserver = useObserver({ key: 'affected-staff-send-contract' })

  const sendContractMutation = useMutation({
    mutationFn: sendContract,
    onSuccess: () => {
      toast.success('Contrat envoyé avec succès')
      sendContractObserver.notify(['update-affected-staff-data'])
      closeModel('contract-change-confirmation')
    },
    onError: showAxiosError
  })

  const contractReminderMutation = useMutation({
    mutationFn: sendContractReminder,
    onSuccess: () => {
      toast.success('Relance du contrat envoyée avec succès')
      sendContractObserver.notify(['update-affected-staff-data'])
    },
    onError: showAxiosError
  })

  const showWarningPopUp = affectedStaff.contractAmount
    ? affectedStaff.contractAmount !== affectedStaff.totalSalary
    : false

  const contractLabelButton: string = useMemo(() => {
    if (!affectedStaff.contract?.contractSentAt) return 'Envoyer le contrat'
    if (showWarningPopUp) return 'Renvoyer le contrat'
    return 'Relancer le contrat'
  }, [affectedStaff.contract?.contractSentAt, showWarningPopUp])

  return (
    <InnerBox className="rounded-lg flex flex-col gap-3">
      <div className="flex justify-between items-center w-full">
        <div className="flex gap-2 items-center">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            Contrat
          </Typography>
          {affectedStaff.contract?.contractSignedAt && <Badge text={'Signé'} variant="success" />}
        </div>
        <div className="flex gap-2 items-center">
          <Button
            type="button"
            icon={PdfIcon}
            variant="secondary"
            size="small"
            className="px-1"
            disabled={!affectedStaff.contract?.documentFile?.path}
            onClick={() => {
              if (!affectedStaff.contract?.documentFile?.path) return
              window.open(affectedStaff.contract.documentFile.path, '_blank')
            }}
          />
          {!affectedStaff.contract?.contractSignedAt && (
            <Button
              type="button"
              icon={SendIcon}
              variant="secondary"
              loading={sendContractMutation.isPending || contractReminderMutation.isPending}
              label={contractLabelButton}
              onClick={() => {
                if (!showWarningPopUp) {
                  // a contract whose email never went out is sent, not reminded
                  if (!affectedStaff.contract?.contractSentAt)
                    sendContractMutation.mutate(affectedStaff.id)
                  else contractReminderMutation.mutate(affectedStaff.id)
                } else toggleModal('contract-change-confirmation')
              }}
            />
          )}
        </div>
      </div>
      <div className="grid grid-cols-3">
        <LabelWithText
          label="Date d'envoi"
          data={
            <Badge
              text={valueRender(affectedStaff.contract?.contractSentAt, (d) =>
                DateFormatter.DDMMYYYY(d)
              )}
            />
          }
        />
        <LabelWithText
          label="Date de relance"
          className="place-self-center"
          data={
            <Badge
              text={valueRender(
                affectedStaff.contractReminders?.at(-1)?.sentAt ??
                  affectedStaff.contract?.contractResentAt,
                (d) => DateFormatter.DDMMYYYY(d)
              )}
            />
          }
        />
        <LabelWithText
          className="place-self-center"
          label="Date de signature"
          data={
            <Badge
              text={valueRender(affectedStaff.contract?.contractSignedAt, (d) =>
                DateFormatter.DDMMYYYY(d)
              )}
            />
          }
        />
      </div>

      <Modal
        isOpen={modalState['contract-change-confirmation']}
        icon={InformationIcon}
        variant="confirmation"
        size="medium"
        isLoading={sendContractMutation.isPending}
        title="Étant donné que vous avez modifié les dates du contrat ou la rémunération, un nouveau contrat sera généré."
        actionButtonsOptions={{
          confirmText: 'OK',
          cancelText: 'Fermer',
          onConfirm: () => sendContractMutation.mutate(affectedStaff.id)
        }}
        onCancel={() => closeModel('contract-change-confirmation')}
      ></Modal>
    </InnerBox>
  )
}

export default AffectedStaffContractSummarySection
